import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, MapPin, PlayCircle, LifeBuoy, Sparkles, Phone, Send, Navigation, ChevronRight, ChevronUp, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, mediaUrl, formatApiErrorDetail, trackEvent } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DialogDescription } from "@/components/ui/dialog";

const SECTIONS = [
  { key: "house-info", to: "/house-info", title: "House Info", desc: "Wi-Fi, check-in & rules", icon: HomeIcon, span: "lg:col-span-6", tone: "forest" },
  { key: "local-tips", to: "/local-tips", title: "Local Spots", desc: "Eat, explore & get around", icon: MapPin, span: "lg:col-span-6", tone: "terracotta" },
  { key: "guides", to: "/guides", title: "How-To Guides", desc: "Appliances made simple", icon: PlayCircle, span: "lg:col-span-4", tone: "light" },
  { key: "help", to: "/help", title: "Need Help?", desc: "Message your host", icon: LifeBuoy, span: "lg:col-span-4", tone: "light" },
  { key: "add-ons", to: "/add-ons", title: "Add-Ons", desc: "Extras & services", icon: Sparkles, span: "lg:col-span-4", tone: "terracotta" },
];

const toneClass = {
  forest: "bg-brand-forest text-brand-bg",
  terracotta: "bg-brand-terracotta text-white",
  light: "bg-white text-brand-forest",
};

const CARD_IMAGES = {
  "house-info": { url: "https://customer-assets-39nsmqrw.emergentagent.net/job_stay-compass-1/artifacts/sgk81s3u_house%20info%20logo.webp", ratio: "3 / 1" },
  "local-tips": { url: "/branding/local-spots-gen.png", ratio: "3 / 1" },
  "add-ons": { url: "/branding/add-ons-gen.png", ratio: "1264 / 848" },
  "guides": { url: "/branding/how-to-guides-gen.png", ratio: "1264 / 848" },
  "help": { url: "/branding/need-help-gen.png", ratio: null },
};

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [property, setProperty] = useState({});
  const [doorOpen, setDoorOpen] = useState(false);
  const [showInstr, setShowInstr] = useState(false);
  const [doorCode, setDoorCode] = useState(null);
  const [accessInput, setAccessInput] = useState("");
  const [doorErr, setDoorErr] = useState("");
  const [blockedMsg, setBlockedMsg] = useState("");
  const [guestName, setGuestName] = useState("");
  const [doorLoading, setDoorLoading] = useState(false);
  const [showSwipe, setShowSwipe] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 48;
      setShowSwipe(!atBottom);
    };
    const t = setTimeout(onScroll, 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("load", onScroll);
    return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); window.removeEventListener("load", onScroll); };
  }, []);

  const openDoor = async () => {
    setShowInstr(false); setAccessInput(""); setDoorErr(""); setDoorCode(null); setBlockedMsg(""); setGuestName("");
    setDoorOpen(true);
    try {
      const { data } = await api.post("/door-code", { access_code: "" });
      if (!data.gated) { setDoorCode(data.door_code); setGuestName(data.guest_name || ""); trackEvent("door_unlock", "", data.guest_name || ""); }
    } catch (e) {
      const st = e.response?.status;
      if (st === 403) setBlockedMsg(formatApiErrorDetail(e.response?.data?.detail) || "The door code isn't available right now.");
      else if (st !== 401) setBlockedMsg("Something went wrong. Please try again in a moment.");
      /* 401 -> gated, show access input */
    }
  };

  const submitAccess = async () => {
    setDoorLoading(true); setDoorErr("");
    try {
      const { data } = await api.post("/door-code", { access_code: accessInput });
      setDoorCode(data.door_code); setGuestName(data.guest_name || ""); trackEvent("door_unlock", "", data.guest_name || "");
    } catch (e) {
      const st = e.response?.status;
      if (st === 403) setBlockedMsg(formatApiErrorDetail(e.response?.data?.detail) || "The door code isn't available right now.");
      else if (st === 401) setDoorErr(formatApiErrorDetail(e.response?.data?.detail) || "Incorrect code");
      else setDoorErr("Something went wrong. Please try again in a moment.");
    } finally { setDoorLoading(false); }
  };

  useEffect(() => {
    if (searchParams.get("door") === "1") {
      openDoor();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const guestUrl = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    api.get("/property").then((r) => setProperty(r.data)).catch(() => {});
  }, []);

  if (!settings) return null;

  const contacts = property.contacts || [];
  const hostPhone = (contacts.find((c) => /host/i.test(c.label)) || contacts[0] || {}).value || "";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-0 sm:py-12">
      {/* Mobile hero (phone view) */}
      <div className="sm:hidden -mx-4 -mt-8 mb-0 relative overflow-hidden" data-testid="mobile-hero">
        <img src="/branding/amazing-stay-phone.png" alt="Property" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-forest/40 via-brand-forest/25 to-brand-forest/70" />
        <div className="relative flex flex-col items-center justify-center text-center px-6 pt-6 pb-6 min-h-[calc(100svh-5rem)]">
          {settings.logo_url && (
            <div className="mb-4">
              <img src="/branding/logo-transparent.png" alt={settings.property_name} data-testid="mobile-hero-logo" className="h-24 w-auto object-contain" style={{ filter: "drop-shadow(1px 0 0 #fff) drop-shadow(-1px 0 0 #fff) drop-shadow(0 1px 0 #fff) drop-shadow(0 -1px 0 #fff) drop-shadow(0 6px 8px rgba(0,0,0,0.35))" }} />
            </div>
          )}
          <div className="flex items-center gap-4 mb-3">
            {hostPhone && (
              <a
                href={`tel:${hostPhone.replace(/[^0-9+]/g, "")}`}
                data-testid="mobile-call"
                className="h-12 w-12 rounded-full border-2 border-brand-ocean text-white flex items-center justify-center active:scale-95 transition-transform"
              >
                <Phone size={22} />
              </a>
            )}
            <button
              onClick={() => navigate("/help")}
              data-testid="mobile-message"
              className="h-12 w-12 rounded-full border-2 border-brand-ocean text-white flex items-center justify-center active:scale-95 transition-transform"
            >
              <Send size={22} />
            </button>
          </div>
          <div className="w-10 h-px bg-white/40 mb-4" />
          <h1 className="font-display text-3xl tracking-tight text-white mb-3 leading-tight">
            {settings.tagline || settings.property_name}
          </h1>
          {property.address && (
            <div className="flex flex-col items-center gap-2 mb-4">
              <div
                data-testid="mobile-address-link"
                className="flex items-start justify-center gap-2 text-white/90"
              >
                <MapPin size={20} className="mt-1 shrink-0 text-brand-ocean" />
                <span className="text-base leading-snug text-center">
                  {(() => {
                    const idx = property.address.indexOf(",");
                    if (idx === -1) return property.address;
                    return (
                      <>
                        {property.address.slice(0, idx).trim()}
                        <br />
                        {property.address.slice(idx + 1).trim()}
                      </>
                    );
                  })()}
                </span>
              </div>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(property.address)}`}
                target="_blank" rel="noreferrer"
                data-testid="mobile-directions"
                className="inline-flex items-center gap-2 rounded-full border border-white/50 text-white px-5 py-2 text-sm font-semibold active:scale-95 transition-transform"
              >
                <Navigation size={16} className="text-brand-ocean" /> Directions
              </a>
            </div>
          )}
          <button
            onClick={() => navigate("/house-info")}
            data-testid="mobile-get-started"
            className="rounded-2xl bg-brand-ocean text-white font-semibold tracking-wide text-base px-10 py-3.5 shadow-lg active:scale-95 transition-transform"
          >
            GET STARTED
          </button>
          <button
            onClick={openDoor}
            data-testid="mobile-door-code-btn"
            className="mt-3 rounded-2xl border-2 border-white/70 text-white font-semibold tracking-wide text-base px-10 py-3.5 active:scale-95 transition-transform"
          >
            My Door Code
          </button>
        </div>
      </div>

      <Dialog open={doorOpen} onOpenChange={(o) => { setDoorOpen(o); if (!o) setShowInstr(false); }}>
        <DialogContent className="rounded-3xl max-w-sm text-center max-h-[85vh] overflow-y-auto" data-testid="door-code-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-center">
              {doorCode !== null
                ? (guestName ? `Welcome, ${guestName.split(" ")[0]}!` : "Your unique door lock code is")
                : blockedMsg ? "Door code unavailable" : "Verify it's you"}
            </DialogTitle>
            <DialogDescription className="sr-only">Enter your access code to view the property door lock code.</DialogDescription>
          </DialogHeader>
          {doorCode !== null ? (
            <>
              <div data-testid="door-code-value" className="font-display text-6xl font-bold tracking-[0.25em] text-brand-terracotta py-3">
                {doorCode || "----"}
              </div>
              <button
                data-testid="door-code-instructions-link"
                onClick={() => setShowInstr((v) => !v)}
                className="inline-flex items-center justify-center gap-1 text-sm font-bold text-brand-forest hover:text-brand-terracotta underline underline-offset-2 active:scale-95 transition-transform mx-auto"
              >
                Check-in instructions <ChevronRight size={15} className={showInstr ? "rotate-90 transition-transform" : "transition-transform"} />
              </button>
              {showInstr && (
                <p data-testid="door-code-instructions" className="text-brand-forest/80 leading-relaxed whitespace-pre-line text-left mt-1">
                  {property.checkin_instructions || "Instructions coming soon."}
                </p>
              )}
            </>
          ) : blockedMsg ? (
            <p data-testid="door-blocked-msg" className="text-brand-forest/70 leading-relaxed pt-1">{blockedMsg}</p>
          ) : (
            <div className="space-y-3 pt-1">
              <p className="text-sm text-brand-forest/60">Enter your first name to access your door code</p>
              <input
                data-testid="door-access-input"
                value={accessInput}
                onChange={(e) => setAccessInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitAccess(); }}
                placeholder="Your first name"
                autoFocus
                className="w-full rounded-xl border border-black/10 px-4 py-3 text-center text-lg focus:outline-none focus:border-brand-terracotta"
              />
              {doorErr && <p data-testid="door-access-error" className="text-sm text-destructive">{doorErr}</p>}
              <button
                data-testid="door-access-submit"
                onClick={submitAccess}
                disabled={doorLoading}
                className="w-full rounded-xl bg-brand-terracotta text-white font-semibold py-3 active:scale-95 transition-transform disabled:opacity-60"
              >
                {doorLoading ? "Checking…" : "Unlock Code"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="hidden sm:block relative overflow-hidden rounded-3xl mb-8 shadow-sm"
        style={{ aspectRatio: "1774 / 887" }}
        data-testid="home-hero"
      >
        <img src="/branding/amazing-stay.png" alt="Your Guide to an Amazing Stay" className="absolute inset-0 h-full w-full object-cover object-center" />

        {/* QR to open on phone */}
        <div data-testid="hero-qr" className="absolute bottom-5 right-5 flex flex-col items-center gap-2">
          <span data-testid="qr-scan-hint" className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-brand-forest shadow-md whitespace-nowrap">
            <Smartphone size={14} className="text-brand-terracotta" /> Phone guide
          </span>
          <div className="bg-white rounded-2xl p-2 shadow-lg overflow-hidden">
            <QRCodeSVG value={guestUrl} size={80} fgColor="#2C3B2E" bgColor="#ffffff" level="M" />
          </div>
        </div>

        {settings.logo_url && (
          <div className="absolute bottom-5 left-5 inline-flex bg-white rounded-2xl p-2 shadow-lg overflow-hidden">
            <img src={mediaUrl(settings.logo_url)} alt={settings.property_name} data-testid="hero-logo" className="h-16 sm:h-20 w-auto object-contain scale-125" />
          </div>
        )}
      </motion.div>

      {/* Bento grid (tablet & desktop) */}
      <div id="home-sections" className="scroll-mt-20 hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6">
        {SECTIONS.map((s, i) => {
          const Icon = s.icon;
          const isImg = !!CARD_IMAGES[s.key];
          const isPair = s.key === "add-ons" || s.key === "help";
          const smSpan = isPair ? "sm:col-span-1" : "sm:col-span-2";
          const orderCls = s.key === "add-ons" ? "sm:order-1 lg:order-1" : s.key === "help" ? "sm:order-2 lg:order-2" : "";
          const imgCardCls = (s.key === "add-ons" || s.key === "help" || s.key === "guides")
            ? "w-full"
            : isPair
            ? "aspect-[2/1] w-full lg:aspect-auto lg:min-h-[200px]"
            : "min-h-[168px]";
          return (
            <motion.button
              key={s.key}
              data-testid={`section-card-${s.key}`}
              onClick={() => navigate(s.to)}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.05 * (i + 1) }}
              style={CARD_IMAGES[s.key]?.ratio ? { aspectRatio: CARD_IMAGES[s.key].ratio } : undefined}
              className={`group text-left rounded-3xl border border-black/5 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] active:scale-[0.98] ${s.span} ${smSpan} ${orderCls} ${isImg ? `relative overflow-hidden bg-[#f9e6c8] p-0 ${imgCardCls}` : `min-h-[168px] p-7 sm:p-8 flex flex-col justify-between ${toneClass[s.tone]}`}`}
            >
              {CARD_IMAGES[s.key] ? (
                <img src={CARD_IMAGES[s.key].url.startsWith("/") ? CARD_IMAGES[s.key].url : mediaUrl(CARD_IMAGES[s.key].url)} alt={s.title} className="absolute inset-0 h-full w-full object-center object-cover" />
              ) : (
                <>
                  <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${s.tone === "light" ? "bg-brand-sand" : "bg-white/15"}`}>
                    <Icon size={28} className={s.tone === "light" ? "text-brand-terracotta" : "text-current"} />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-1">{s.title}</h2>
                    <p className={`text-sm ${s.tone === "light" ? "text-brand-forest/60" : "opacity-80"}`}>{s.desc}</p>
                  </div>
                </>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Scroll nudge — tablet/desktop only */}
      {showSwipe && (
        <div data-testid="swipe-up-hint" className="hidden sm:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex-col items-center gap-1 text-brand-forest animate-fade-inout">
          <ChevronUp size={40} className="animate-bounce text-brand-terracotta" />
          <span className="text-lg font-bold uppercase tracking-[0.25em]">Swipe up</span>
        </div>
      )}

    </div>
  );
}
